// src/components/ClaimLanguageHelper.js
//
// RULING 265 (Bob, 2026-09-30): "how do we allow the claimant to speak freely, in terms he understands and then
// translate it to proper, appropriate, legalese." Two buttons under any box where a person writes what happened:
//   Check spelling and grammar  - his own words, corrected; nothing added.
//   Put it in claim language    - a formal Statement of Claim from his words and the claim record only.
// The answer is shown BESIDE his words, never in place of them. A name, date or number the assistant used that is not
// in his words or on the claim is marked in red, and "Use this" stays locked until he has removed or kept each one on
// purpose. Nothing is written by this component; the parent decides what "Use this" does.
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { markUnsupported, stillUnsupported } from '../data/claimLanguage';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', panel: '#0D1B2A' };

export default function ClaimLanguageHelper({ text, facts, modes, onUse, acceptLabel }) {
  const m = modes || ['proofread', 'statement'];
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [res, setRes] = useState(null);         // { mode, text, unsupported }
  const [draft, setDraft] = useState('');
  const [kept, setKept] = useState(false);
  const run = async (mode) => {
    setErr(''); setRes(null); setBusy(mode); setKept(false);
    try {
      const fn = httpsCallable(getFunctions(), 'claimLanguage');
      const r = await fn({ mode, text: String(text || ''), facts: facts || {} });
      setRes(r.data); setDraft(r.data.text);
    } catch (e) {
      setErr((e && e.message) || 'The language service did not answer. Your words are unchanged.');
    }
    setBusy('');
  };
  const left = res ? stillUnsupported(draft, res.unsupported) : [];
  const locked = left.length > 0 && !kept;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {m.includes('proofread') ? <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy || String(text || '').trim().length < 5} onClick={() => run('proofread')}>{busy === 'proofread' ? 'Checking…' : 'Check spelling and grammar'}</button> : null}
        {m.includes('statement') ? <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy || String(text || '').trim().length < 5} onClick={() => run('statement')}>{busy === 'statement' ? 'Writing…' : 'Put it in claim language'}</button> : null}
      </div>
      {err ? <div style={{ color: C.bad, fontSize: 12, marginTop: 6 }}>&#9888; {err}</div> : null}
      {res ? (
        <div style={{ border: '1px solid ' + C.accent, background: C.panel, borderRadius: 6, padding: '10px 12px', marginTop: 8 }}>
          <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            {res.mode === 'proofread' ? 'Your words, spelling and grammar corrected' : 'Your account in claim language - read it, change anything that is not right'}
          </div>
          <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: 8 }}>
            {markUnsupported(draft, res.unsupported).map((p, i) => p.bad
              ? <mark key={i} title="Not in your words or on the claim" style={{ background: 'transparent', color: C.bad, fontWeight: 700, textDecoration: 'underline' }}>{p.text}</mark>
              : <span key={i}>{p.text}</span>)}
          </div>
          <textarea aria-label="Your draft wording" className="form-textarea" rows={5} spellCheck value={draft} onChange={e => setDraft(e.target.value)} style={{ fontSize: 13 }} />
          {left.length ? (
            <div style={{ fontSize: 12, color: C.bad, marginTop: 6 }}>
              These are not in your words or on the claim: <b>{left.join(', ')}</b>. Take them out, or tick the box if they are true.
              <label style={{ display: 'block', color: C.text, marginTop: 4, cursor: 'pointer' }}><input aria-label="They are true - I am keeping them" type="checkbox" checked={kept} onChange={e => setKept(e.target.checked)} /> They are true - I am keeping them</label>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={locked || !draft.trim()} onClick={() => { onUse && onUse(draft.trim(), res.mode); setRes(null); }}>{acceptLabel ? acceptLabel(res.mode) : 'Use this'}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRes(null)}>Keep my own words</button>
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>Nothing changes until you choose. What you first wrote stays on your claim exactly as you wrote it.</div>
        </div>
      ) : null}
    </div>
  );
}
